/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Container, Col, Row, Form, Button, Modal, Spinner } from 'react-bootstrap';
import { DokaImageEditor } from 'react-doka';
import Axios from 'axios';

// project imports
import { IMAGE_TYPES } from 'shared/settings/config';
import { getBuyersUrl, getBuyerHeaders } from 'shared/http';
import { mobileNumberFormat } from 'shared/modifiers';
import { setEditorPlugins, getEditorDefaults, circleCropMask } from 'shared/doka';
import { setUser } from 'shared/settings/auth';
import store from 'features/store';
import Validators from 'shared/validators';

// style imports
import 'doka/doka.css';
import './Profile.scss';
import { buyerUserDetail, emailChange, verifyEmail } from 'shared/settings/apiconfig';
import authService from 'shared/services/authService';

/* initializes doka editor plugins */
setEditorPlugins();

/**
 * this page is used to edit basic user details
 * 
 * @param props
 * 
 * @returns html code for rendering profile page
 * 
**/

class Profile extends Component {

    formEdited;
    location;
    confirmedNavigation;
    customEditorDefaults;
    profileData;

    constructor(props) {
        super(props);

        this.state = {
            // image
            profileImage: {
                modal: false,
                imgSrc: null,
                tempImg: null
            },

            // form
            profileFormControls: {
                firstName: { value: '', error: '', validators: [Validators.required(), Validators.maxLength(45)] },
                lastName: { value: '', error: '', validators: [Validators.required(), Validators.maxLength(45)] },
                email: { value: '', error: '', validators: [] },
                mobile: { value: '', error: '', validators: [Validators.required(), Validators.mobile(), Validators.maxLength(15)] },
            },
            profileFormValid: false,

            // change email
            newEmail: { value: '', error: '', validators: [Validators.required(), Validators.email()] },
            emailModal: false,
            unverifiedemail: null,
            emailverified: 0,
            isemailchanged: true,

            // loading
            pageLoading: true,
            profileFormLoading: false,
            emailLoading: false,
            verifyLoading: false,

            showNavigateDialog: false
        };

        this.location = [];
        this.formEdited = false;
        this.confirmedNavigation = false;
        this.customEditorDefaults = {
            imageCropAspectRatio: 1,
            cropEnableSelectPreset: false,
            imageCropMinSize: { width: 130, height: 130 },
            willRenderCanvas: (shapes, state) => circleCropMask(shapes, state)
        };
        this.profileData = null;
    }

    componentDidMount() {
        this.getProfileData();
    }

    /* get user profile data */
    getProfileData = async () => {
        await Axios.get(getBuyersUrl(`${buyerUserDetail}/${this.props.user.id}`), getBuyerHeaders())
            .then(response => {
                this.profileData = { ...response.data.data[0] };
                this.setState({
                    profileFormControls: {
                        firstName: { ...this.state.profileFormControls.firstName, value: this.profileData.firstname },
                        lastName: { ...this.state.profileFormControls.lastName, value: this.profileData.lastname },
                        email: { ...this.state.profileFormControls.email, value: this.profileData.email },
                        mobile: { ...this.state.profileFormControls.mobile, value: this.profileData.mobile ? mobileNumberFormat(this.profileData.mobile, this.profileData.mobile, true) : null }
                    },
                    profileFormValid: true,
                    profileImage: { ...this.state.profileImage, imgSrc: this.profileData.url },
                    pageLoading: false,
                    unverifiedemail: this.profileData.unverifiedemail,
                    emailverified: this.profileData.emailverified,
                    newEmail: { ...this.state.newEmail, value: this.profileData.unverifiedemail ? this.profileData.unverifiedemail : null },
                    isemailchanged: this.profileData.unverifiedemail ? false : true
                });
            })
            .catch(error => {
                this.setState({ pageLoading: false });
                // TODO: Error message handle
            });
    };

    /* event to trigger file input button internally */
    onClickFileInput = () => {
        document.getElementById('img-profile').click();
    };

    onUploadImage = (event) => {
        this.setState({
            profileImage: {
                ...this.state.profileImage,
                tempImg: URL.createObjectURL(event.target.files[0]),
                modal: true
            }
        });

        // reset input to upload same image
        event.target.value = '';
    };

    onCancelImage = () => {
        this.setState({
            profileImage: {
                ...this.state.profileImage,
                modal: false,
                tempImg: null
            }
        });
    };

    onSaveImage = (output) => {
        this.setState({
            profileImage: {
                ...this.state.profileImage,
                imgSrc: URL.createObjectURL(output.dest),
                modal: false
            }
        });

        // used to convert image to base 64
        let render = new FileReader();

        // trigger once image is loaded into FileReader
        render.onloadend = async () => {
            const data = {
                id: this.props.user.id,
                image: render.result.replace('data:', '').replace(/^.+,/, ''),
            };
            authService.profileUpdate({ data: data }).then(response => {
                store.dispatch({
                    type: 'SET_USER',
                    payload: {
                        profileImage: response.data['data'][0]['url'],
                        id: this.props.user.id
                    }
                });
                // TODO: success message handle
                setUser(this.props.user.id);
            }).catch(error => {
                this.setState({ profileFormLoading: false });
                // TODO: Error message handle
            });
        };

        render.readAsDataURL(output.dest);
    };

    onEmailChange = (event) => {
        let inputError = '';

        /* check validation */
        for (let validator of this.state.newEmail.validators) {
            inputError = validator(event.target.value);
            if (inputError) break;
        }

        this.setState({
            newEmail: {
                ...this.state.newEmail,
                value: event.target.value,
                error: inputError
            }
        });
    };

    showEmailModal = () => {
        this.setState({
            emailModal: true
        });
    };

    hideEmailModal = () => {
        this.setState({ emailModal: false }, () => this.getProfileData());
    };

    /* request to re-send verify email for new email */
    verifyNewEmail = async () => {
        this.setState({ verifyLoading: true })
        await Axios.get(getBuyersUrl(verifyEmail), getBuyerHeaders())
            .then(response => {
                // TODO: success message handle
            })
            .catch(error => {
                // TODO: Error message handle
            })
            .finally(() => {
                this.setState({ verifyLoading: false, emailModal: false }, () => this.getProfileData());
            });
    };

    saveNewEmail = async () => {
        if (this.state.unverifiedemail === this.state.newEmail.value && !this.state.isemailchanged) {
            this.setState({ isemailchanged: true });
        }
        else {
            if (!this.state.newEmail.error) {
                const data = {
                    data: {
                        newemail: this.state.newEmail.value
                    }
                };

                this.setState({ emailLoading: true });
                await Axios.put(getBuyersUrl(emailChange), JSON.stringify(data), getBuyerHeaders())
                    .then(response => {
                        this.setState({
                            profileFormControls: {
                                ...this.state.profileFormControls,
                                email: {
                                    ...this.state.profileFormControls.email,
                                    value: this.state.newEmail.value
                                }
                            },
                            emailLoading: false,
                            emailModal: false,
                            unverifiedemail: this.state.newEmail.value,
                            emailverified: 0
                        }, () => this.getProfileData());
                        this.profileData['unverifiedemail'] = this.state.newEmail.value;
                        this.profileData['emailverified'] = 0;
                        // TODO: success message handle
                    })
                    .catch(error => {
                        this.setState({ emailLoading: false });
                        // TODO: Error message handle
                    });
            }
        }
    };

    /* check validation for input changes */
    omInputValidation = (event) => {
        this.formEdited = true;

        if (event.target.name === 'mobile') {
            const event_value = mobileNumberFormat(event.target.value, this.state.profileFormControls.mobile.value);
            event.target.value = event_value ? event_value : event.target.value;
        }

        const validatedForm = Validators.formValidation({ ...this.state.profileFormControls }, event);

        this.setState({
            profileFormControls: validatedForm['formControls'],
            profileFormValid: validatedForm['formValid']
        });
    };

    /* block the navigation when form edited */
    blockNavigation = (nextLocation) => {
        if (this.formEdited && !this.confirmedNavigation) {
            if (!this.state.showNavigateDialog)
                this.setState({ ...this.state, showNavigateDialog: true });
            this.location = nextLocation;
            return false;
        }
        return true;
    };

    /* called when user clicked discard */
    navigateConfirm = () => {
        if (this.state.showNavigateDialog) {
            this.setState({ ...this.state, showNavigateDialog: false });
            this.confirmedNavigation = true;
            this.props.history.push(this.location.pathname);
        }

    };

    saveProfileForm = async () => {
        const data = {};
        this.setState({ profileFormLoading: true });
        Object.entries(this.state.profileFormControls).forEach(([key, value]) => data[key.toLowerCase()] = value['value']);

        delete data['valid'];
        data['id'] = this.props.user.id;
        /* update profile data */
        authService.profileUpdate({ data: data }).then(response => {
            if (response.status === 200) {
                // TODO: success message handle
                this.setState({ profileFormLoading: false }, () => this.getProfileData());
                setUser(this.props.user.id);
            }
        }).catch(error => {
            this.setState({ profileFormLoading: false });
            // TODO: Error message handle
        });
    };

    render() {

        return (
            <div className="d-flex flex-column" style={{ overflowY: 'overlay' }}>
                <div className="d-flex flex-row align-items-center border-bottom border-outline" style={{ minHeight: '40px' }}>
                    <span className="text-secondary mx-2">My Profile</span>
                </div>
                {
                    this.state.pageLoading ?
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                            <Spinner animation="border" variant="info" className="spinner-3x" />
                        </div> :
                        <Container className="profile-container">
                            <Row>
                                <Col md={4} xs={{ span: 12, order: 1 }}>
                                    <div className="d-flex flex-column justify-content-center align-items-center profile-background profile-image">
                                        <div className="d-flex flex-row justify-content-center profile-rounded">
                                            {
                                                this.state.profileImage.imgSrc ?
                                                    <img src={this.state.profileImage.imgSrc} alt="invalid profile" height="135" width="135" /> :
                                                    <img src={`${process.env.PUBLIC_URL}/static/icons/profile/default_2.svg`} alt="default" style={{ marginTop: '26px' }} height="75" width="75" />
                                            }
                                        </div>
                                        <Form.Control id="img-profile" type="file" onChange={this.onUploadImage} accept={IMAGE_TYPES} hidden />
                                        <span className="mt-2 text-secondary" style={{ cursor: 'pointer' }} onClick={this.onClickFileInput}>
                                            {this.state.profileImage.imgSrc ? 'Change Image' : 'Add Image'}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={8} xs={{ span: 12, order: 2 }}>
                                    <div className="profile-form">
                                        <Form className="px-5 py-2">
                                            <div>
                                                <div className="float-control">
                                                    <div className="text">
                                                        {
                                                            this.state.profileFormControls.firstName.error ? <small className="error">{this.state.profileFormControls.firstName.error}</small> : null
                                                        }
                                                    </div>
                                                    <Form.Control type="text" id="firstName" name="firstName" placeholder=" " value={this.state.profileFormControls.firstName.value} onChange={this.omInputValidation} />
                                                    <label htmlFor="firstName" className="required">First Name *</label>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="float-control">
                                                    <div className="text">
                                                        {
                                                            this.state.profileFormControls.lastName.error ? <small className="error">{this.state.profileFormControls.lastName.error}</small> : null
                                                        }
                                                    </div>
                                                    <Form.Control type="text" id="lastName" name="lastName" placeholder=" " value={this.state.profileFormControls.lastName.value} onChange={this.omInputValidation} />
                                                    <label htmlFor="lastName" className="required">Last Name *</label>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="d-flex flex-row justify-content-end">
                                                    <Button variant="warning" className="btn-email text-white"
                                                        disabled={this.state.verifyLoading} onClick={this.showEmailModal}>Change Email</Button>
                                                </div>
                                                <div className="float-control">
                                                    <Form.Control type="text" id="email" name="email" placeholder=" " value={this.state.profileFormControls.email.value} onChange={this.omInputValidation} disabled />
                                                    <label htmlFor="email" className="required">Email *</label>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="float-control">
                                                    <div className="text">
                                                        {
                                                            this.state.profileFormControls.mobile.error && this.state.profileFormControls.mobile.error.length ? <small className="error">{this.state.profileFormControls.mobile.error}</small> : null
                                                        }
                                                    </div>
                                                    <Form.Control type="text" id="mobile" name="mobile" placeholder=" " value={this.state.profileFormControls.mobile.value} onChange={this.omInputValidation} />
                                                    <label htmlFor="mobile" className="required">Phone Number *</label>
                                                </div>
                                            </div>
                                            <Row className="py-4">
                                                <Col xs={6} md={6}>
                                                    <Link to='/'>
                                                        <Button type="button" variant="outline-primary" className="cancel-button">Cancel</Button>
                                                    </Link>
                                                </Col>
                                                <Col xs={6} md={6}>
                                                    <Button type="button" variant="secondary" className="save-button" disabled={!this.state.profileFormValid || this.state.profileFormLoading}
                                                        onClick={this.saveProfileForm}>Save</Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                        <div className="note px-2 pb-3 text-center">
                                            <small>Important: If you changed your email address,you will be logged out and you must validate your new email address befor you can log in</small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                }

                {/* dialog box for upprofileFormLoading profile image */}
                <Modal show={this.state.profileImage.modal} size="lg" animation={false} backdrop="static" onHide={this.onCancelImage}>
                    <Modal.Header closeButton></Modal.Header>
                    <Modal.Body className="d-flex flex-column align-items-center justify-content-center">
                        <div style={{ width: '100%', height: '400px', maxHeight: '80vw' }}>
                            <DokaImageEditor {...getEditorDefaults({ width: 130, height: 130 })}
                                {...this.customEditorDefaults} src={this.state.profileImage.tempImg}
                                onProcess={this.onSaveImage} />
                        </div>
                    </Modal.Body>
                </Modal>

                {/* modal to change email */}
                <Modal centered show={this.state.emailModal} size="md" animation={false} backdrop="static" keyboard={false}>
                    <Modal.Body>
                        <div className="d-flex flex-column justify-content-center align-items-center w-100">
                            <div className="float-control w-75">
                                <div className="text">
                                    {
                                        this.state.newEmail.error ? <small className="error">{this.state.newEmail.error}</small> : null
                                    }
                                </div>
                                <Form.Control autoFocus type="text" id="new-email" name="newEmail" placeholder=" "
                                    disabled={(this.state.emailLoading || this.state.unverifiedemail === this.state.newEmail.value) && !this.state.isemailchanged} value={this.state.newEmail.value} onChange={this.onEmailChange} />
                                <label htmlFor="newEmail" className="required">New Email *</label>
                            </div>
                            <div className="d-flex flex-column flex-sm-row mt-4">
                                <Button variant="outline-dark btn-white mx-2" onClick={this.hideEmailModal}
                                    disabled={this.state.emailLoading}>Cancel</Button>
                                <div className="d-flex flex-row justify-content-center align-items-center mt-2 mt-sm-0">
                                    <Button variant="secondary mx-2" onClick={this.saveNewEmail}
                                        disabled={this.state.newEmail.error || this.state.emailLoading}>{this.state.unverifiedemail && !this.state.isemailchanged ? 'Edit' : 'Submit'}</Button>
                                    {
                                        this.state.emailLoading ?
                                            <Spinner animation="border" variant="info" /> :
                                            null
                                    }
                                </div>
                                {
                                    this.state.unverifiedemail && !this.state.isemailchanged ?
                                        <div className="d-flex flex-row justify-content-center align-items-center mt-2 mt-sm-0">

                                            <Button variant="secondary mx-2"
                                                disabled={this.state.verifyLoading} onClick={this.verifyNewEmail}>Resend Verification Link</Button>
                                        </div> : null
                                }
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
                {/* <Prompt when={this.formEdited} message={this.blockNavigation} />
                {
                    this.state.showNavigateDialog ?
                        <Modal className="confirm-modal" show={this.state.showNavigateDialog} onHide={() => this.setState({ ...this.state, showNavigateDialog: false })}>
                            <Modal.Header style={{ border: "none" }} closeButton ></Modal.Header>
                            <Modal.Body className="confirm-modal-body">
                                Are you sure you want to discard the changes?
                                <Row style={{ marginTop: "5%" }}>
                                    <Col xs={6} md={6}>
                                        <Button className="cancel-button" onClick={() => this.setState({ ...this.state, showNavigateDialog: false })}>No,wait</Button>
                                    </Col>
                                    <Col xs={6} md={6}>
                                        <Button className="save-button" onClick={() => this.navigateConfirm()}>Yes,Discard it</Button>
                                    </Col>
                                </Row>
                            </Modal.Body>
                        </Modal> : null
                } */}
            </div>
        );
    }
}

export default connect(state => state)(Profile);
